import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import useTranslation from "../../../hooks/useTranslation";
import { getVocabularyPreviewTheme } from "../../../utils/vocabularyPreviewTheme";
import { HARDCODED_OVERVIEW_TRAINER_WORDS } from "../../../components/OverviewTrainerWord";
import { OVERVIEW_NATIVE_LANGUAGE_LABELS } from "../index";

type Direction = "WORD_TO_TRANSLATION" | "TRANSLATION_TO_WORD";

const PROTOTYPE_DETAILS: Record<
  string,
  {
    similarWords: string[];
    surfaceForm: string;
    baseForm: string;
    sourceSentence: string;
    partOfSpeech: string;
    tense: string | null;
    gender: string | null;
    number: string | null;
  }
> = {
  ENGLISH: {
    similarWords: ["detailed", "thorough", "intricate"],
    surfaceForm: "elaborate",
    baseForm: "elaborate",
    sourceSentence:
      "She gave an elaborate explanation of the project timeline.",
    partOfSpeech: "adjective",
    tense: null,
    gender: null,
    number: null,
  },
  GERMAN: {
    similarWords: ["vorsichtig", "achtsam", "sanft"],
    surfaceForm: "behutsam",
    baseForm: "behutsam",
    sourceSentence: "Er öffnete behutsam die Tür, um niemanden zu wecken.",
    partOfSpeech: "adverb",
    tense: null,
    gender: null,
    number: null,
  },
  FRENCH: {
    similarWords: ["fleurissant", "rayonnant", "radieux"],
    surfaceForm: "épanoui",
    baseForm: "épanouir",
    sourceSentence: "Elle était épanouie après cette belle journée au soleil.",
    partOfSpeech: "adjective",
    tense: "past participle",
    gender: "feminine",
    number: "singular",
  },
  ITALIAN: {
    similarWords: ["cagnolino", "cucciolo", "bestiola"],
    surfaceForm: "cane",
    baseForm: "cane",
    sourceSentence: "Il cane corre felice nel parco ogni mattina.",
    partOfSpeech: "noun",
    tense: null,
    gender: "masculine",
    number: "singular",
  },
};

export const Route = createFileRoute("/app/overview-trainer/$languageId")({
  component: OverviewTrainerPage,
});

function OverviewTrainerPage() {
  const { languageId } = Route.useParams();
  const { translate } = useTranslation();
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [direction, setDirection] = useState<Direction>("WORD_TO_TRANSLATION");
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const data = HARDCODED_OVERVIEW_TRAINER_WORDS[languageId];
  if (!data) {
    return (
      <div className="px-4 py-24 text-center">
        <p className="font-serif text-lg text-slate-400">
          {translate("spaced_preview_queue_empty")}
        </p>
      </div>
    );
  }

  const theme = getVocabularyPreviewTheme(languageId);
  const questionText =
    direction === "TRANSLATION_TO_WORD" ? data.translation : data.word;
  const answerText =
    direction === "TRANSLATION_TO_WORD" ? data.word : data.translation;
  const details = PROTOTYPE_DETAILS[languageId];

  const handleRating = (rating: string) => {
    console.log(`[OverviewTrainerPage] rated "${questionText}" as "${rating}"`);
    window.history.back();
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 sm:py-24">
      <p className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-400">
        {OVERVIEW_NATIVE_LANGUAGE_LABELS[languageId] ?? languageId}
      </p>

      <div className="mt-10">
        <div className="flex items-start justify-between gap-6">
          <p
            className="font-serif text-5xl leading-[1.08] tracking-[-0.03em] sm:text-6xl sm:leading-[1.06]"
            style={{ color: theme.text }}
          >
            {questionText}
          </p>
          <button
            type="button"
            onClick={() =>
              setDirection((prev) =>
                prev === "WORD_TO_TRANSLATION"
                  ? "TRANSLATION_TO_WORD"
                  : "WORD_TO_TRANSLATION",
              )
            }
            className="mt-3 flex shrink-0 items-center justify-center rounded-full p-2 text-slate-300 transition-colors hover:bg-slate-50 hover:text-slate-500"
            aria-label={translate("vocabulary_preview_direction_toggle_inline")}
          >
            <ArrowsRightLeftIcon className="h-5 w-5" />
          </button>
        </div>

        <hr className="mt-8 border-slate-100" />

        <p className="mt-6 font-serif text-lg leading-relaxed text-slate-400 italic">
          {translate("vocabulary_guess_prompt")}
        </p>

        <div className="mt-8">
          <button
            type="button"
            onClick={() =>
              setAnswerRevealed((prev) => !prev)
            }
            className="w-full rounded-none border-b border-slate-200 px-0 py-6 text-left transition-colors hover:border-slate-300"
          >
            <p className={`font-serif text-3xl font-normal leading-[1.15] tracking-[-0.02em] text-slate-900 sm:text-4xl ${answerRevealed ? "" : "invisible"}`}>
              {answerText}
            </p>
            <span className="mt-6 block text-[0.68rem] font-medium uppercase tracking-[0.14em] text-slate-400">
              {translate(
                answerRevealed
                  ? "vocabulary_guess_tap_to_hide_hint"
                  : "vocabulary_guess_tap_to_reveal_hint",
              )}
            </span>
          </button>
        </div>

        <hr className="mt-10 border-slate-100" />

        <div className="mt-8">
          <p className="font-serif text-base text-slate-500">
            {translate("did_you_know_the_word")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => handleRating("forgotten")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm transition-colors hover:border-red-300 hover:bg-red-50/50"
            >
              <HandThumbDownIcon className="h-4 w-4 text-red-500" />
              <span className="text-slate-700">
                {translate("didnt_know") || "Didn't know"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleRating("almost")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm transition-colors hover:border-amber-300 hover:bg-amber-50/50"
            >
              <ArrowPathIcon className="h-4 w-4 text-amber-500" />
              <span className="text-slate-700">
                {translate("almost") || "Almost"}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleRating("known")}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm transition-colors hover:border-emerald-300 hover:bg-emerald-50/50"
            >
              <HandThumbUpIcon className="h-4 w-4 text-emerald-500" />
              <span className="text-slate-700">
                {translate("knew_it") || "Knew it"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {details && (
        <div className="mt-20">
          <hr className="border-slate-100" />

          <button
            type="button"
            onClick={() => setDetailsExpanded((prev) => !prev)}
            className="mt-8 flex w-full items-center justify-between gap-3 py-2 text-left transition-colors hover:opacity-70"
          >
            <span className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-400">
              {translate("vocabulary_more_structure")}
            </span>
            <ChevronDownIcon
              className={`h-4 w-4 shrink-0 text-slate-300 transition-transform ${detailsExpanded ? "rotate-180" : ""}`}
            />
          </button>

          {detailsExpanded && (
            <div className="mt-6 space-y-10">
              {details.similarWords.length > 0 && (
                <div>
                  <p className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-400">
                    {translate("similar_words_section_title")}
                  </p>
                  <p className="mt-3 font-serif text-base leading-relaxed text-slate-600">
                    {details.similarWords.join(", ")}
                  </p>
                </div>
              )}

              <div>
                <p className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-400">
                  {translate("vocabulary_label_base_form")}
                </p>
                <p className="mt-1 font-serif text-lg text-slate-800">
                  {details.baseForm}
                </p>
              </div>

              {details.sourceSentence && (
                <div>
                  <p className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-400">
                    {translate("vocabulary_label_source_sentence")}
                  </p>
                  <p className="mt-2 max-w-xl font-serif text-base leading-relaxed italic text-slate-600">
                    {details.sourceSentence}
                  </p>
                </div>
              )}

              <div>
                <p className="text-[0.68rem] font-medium uppercase tracking-[0.18em] text-slate-400">
                  {translate("vocabulary_label_grammar")}
                </p>
                <p className="mt-3 font-serif text-sm leading-relaxed text-slate-600">
                  {[
                    `${translate("vocabulary_label_pos")}: ${details.partOfSpeech}`,
                    details.tense && `Tense: ${details.tense}`,
                    details.gender && `Gender: ${details.gender}`,
                    details.number && `Number: ${details.number}`,
                  ]
                    .filter(Boolean)
                    .join("  ·  ")}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
